import { useState } from "react";
import type { UseFormSetValue } from "react-hook-form";
import type { ExpenseFormEntry } from "web_myplann/Finances/Forms/expenseEntrySchema";
import type { ExpenseAmountFormModel } from "web_myplann/Finances/Forms/useExpenseAmountForm";
import type { ExpenseFormModel } from "web_myplann/Finances/Forms/useExpenseV2Form";
import { findReplacedTotalIndex } from "web_myplann/Finances/Helpers/findReplacedTotalIndex";
import { getAvailableExpenseNames } from "web_myplann/Finances/Helpers/getAvailableExpenseNames";
import type { ExpenseEntry } from "web_myplann/Finances/Types/ExpenseEntry";
import type {
	ClientFinancialPlanExpenseNameEnum,
	ClientFinancialPlanExpenseTypeEnum,
} from "web_myplann/Infrastructure/Api/Api";
import { useListItemEditor } from "web_myplann/Shared/Hooks/useListItemEditor";

type Params = {
	expenses: ExpenseFormEntry[];
	expensesReplace: (expenses: ExpenseFormEntry[]) => void;
	setValue: UseFormSetValue<ExpenseFormModel>;
};

export const useExpenseEditor = ({
	expenses,
	expensesReplace,
	setValue,
}: Params) => {
	const [pickerType, setPickerType] =
		useState<ClientFinancialPlanExpenseTypeEnum | null>(null);
	const [pending, setPending] = useState<ExpenseEntry | null>(null);
	const {
		editingIndex,
		editingItem,
		isEditing,
		initialValues,
		startEdit,
		stopEdit,
	} = useListItemEditor(expenses, (expense) => ({
		monthlyPayment: expense.monthlyPayment,
		sustainableAmount: expense.sustainableAmount || null,
	}));

	const amountTarget: ExpenseEntry | null = editingItem
		? { type: editingItem.type, name: editingItem.name }
		: pending;

	const availableNames = pickerType
		? getAvailableExpenseNames(expenses, pickerType)
		: [];

	const openPicker = (type: ClientFinancialPlanExpenseTypeEnum) =>
		setPickerType(type);

	const closePicker = () => setPickerType(null);

	const selectName = (name: ClientFinancialPlanExpenseNameEnum) => {
		if (!pickerType) return;
		setPending({ type: pickerType, name });
		setPickerType(null);
	};

	const closeAmount = () => {
		setPending(null);
		stopEdit();
	};

	const submitAmount = ({
		monthlyPayment,
		sustainableAmount,
	}: ExpenseAmountFormModel) => {
		if (editingIndex !== null) {
			setValue(`entries.${editingIndex}.monthlyPayment`, monthlyPayment);
			setValue(
				`entries.${editingIndex}.sustainableAmount`,
				sustainableAmount ?? 0,
			);
		} else if (pending) {
			const replacedTotalIndex = findReplacedTotalIndex(
				expenses,
				pending.type,
				pending.name,
			);
			expensesReplace([
				...expenses.filter((_, index) => index !== replacedTotalIndex),
				{
					id: null,
					type: pending.type,
					name: pending.name,
					monthlyPayment,
					sustainableAmount: sustainableAmount ?? 0,
				},
			]);
		}
		closeAmount();
	};

	return {
		pickerType,
		availableNames,
		openPicker,
		closePicker,
		selectName,
		amountTarget,
		isEditing,
		initialValues,
		openEdit: startEdit,
		closeAmount,
		submitAmount,
	};
};
